import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { theme, Text } from '@pfmam/pfmam';
import { Box } from '../../styles/globalStyle';
import { DatePicker, Select } from 'antd';
import moment from 'moment';
import { OverrideStyle } from '../../common/components/datePicker';

const { Option } = Select;

const RowWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  border-bottom: 1px solid ${theme.colors.grey};
  &:last-of-type {
    border-bottom: 0;
  }
`;

const RequiredAsterisk = styled.span`
  color: ${theme.colors.danger_red};
  font-size: ${theme.font_size.size_xl};
  line-height: 24px;
  padding: 14px 0 10px 2px;
`;

const EditableValue = styled.div`
  flex: 1;
  text-align: right;
  padding: 0 0 0 16px;
  cursor: ${(props) => (props.editable ? 'text' : 'default')};
  position: relative;
`;

const InlineInput = styled.input`
  width: 100%;
  text-align: right;
  border: none;
  border-bottom: 2px solid ${theme.colors.primary_color || '#003865'};
  outline: none;
  background: transparent;
  font-size: ${theme.font_size.size_xl};
  line-height: 20px;
  color: ${theme.colors.black_secondary};
  padding: 14px 0 14px 0;
  font-family: 'Inter', sans-serif;
`;

const EditHint = styled.span`
  font-size: 10px;
  color: ${theme.colors.grey_tertiary};
  position: absolute;
  right: 0;
  top: 2px;
  opacity: 0;
  transition: opacity 0.15s;
  pointer-events: none;
  white-space: nowrap;

  ${RowWrapper}:hover & {
    opacity: 1;
  }
`;

const InlineError = styled.div`
  font-size: ${theme.font_size.size_l};
  line-height: 18px;
  color: ${theme.colors.danger_red};
  text-align: right;
  padding: 2px 0 10px 0;
`;

export const DUMMY_DETAILS = [
  { label: 'Security Description', value: '', required: false },
  { label: 'Linked PFMAM Account', value: '', required: false },
  { label: 'Monthly Balance', value: '', required: true },
  { label: 'Issuer Ticker', value: 'FHMS', required: false },
  { label: 'Accrued Interest', value: '$2,116.79', required: true },
  { label: 'YTM at Cost', value: '1.78', required: false },
  { label: 'YTM Market', value: '5.24', required: true },
  { label: 'SEC Type', value: 'ACMBS', required: false },
  { label: 'Maturity Date', value: '8/1/2022', required: false },
  { label: 'S&P Rating', value: 'AA+', required: true },
  { label: 'Moody Rating', value: 'Aaa', required: true },
  { label: 'Fitch Rating', value: 'AAA', required: true },
  { label: 'WAM', value: '0.0894', required: true },
  { label: 'Effective Duration', value: '0.0894', required: true },
  { label: 'Last Updated', value: 'Oct 30, 2023', required: false },
  { label: 'Include In Monthly Statement', value: 'No', required: false },
];

const RATING_FIELD_TO_KEY = {
  'S&P Rating': 'sp',
  'Moody Rating': 'moody',
  'Fitch Rating': 'fitch',
};

const EXTRA_EDITABLE_FIELDS = new Set([
  'Issuer Ticker',
  'YTM at Cost',
  'Maturity Date',
  'S&P Rating',
  'Moody Rating',
  'Fitch Rating',
]);

const sanitizeNumericWithScale = (value, maxIntegerDigits = 12, maxDecimals = 2) => {
  let val = String(value || '').replace(/[^0-9.]/g, '');
  const parts = val.split('.');
  if (parts.length > 2) {
    val = parts[0] + '.' + parts.slice(1).join('');
  }

  const normalized = val.split('.');
  const intPart = (normalized[0] || '').slice(0, maxIntegerDigits);
  const decimalPart = normalized[1] !== undefined ? normalized[1].slice(0, maxDecimals) : undefined;

  return decimalPart !== undefined ? `${intPart}.${decimalPart}` : intPart;
};

const sanitizeNumericOnlyText = (value, maxLength = 49) => {
  let val = String(value || '').replace(/[^0-9.]/g, '');
  const parts = val.split('.');
  if (parts.length > 2) {
    val = parts[0] + '.' + parts.slice(1).join('');
  }

  return val.slice(0, maxLength);
};

const formatNumericWithCommas = (value, maxDecimals = 2) => {
  const raw = String(value || '').replace(/,/g, '');
  if (!raw) return '';
  const parts = raw.split('.');
  const intPart = (parts[0] || '').replace(/^0+(?=\d)/, '') || '0';
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (parts[1] !== undefined) {
    return `${formattedInt}.${parts[1].slice(0, maxDecimals)}`;
  }
  return formattedInt;
};

const sanitizeByField = (label, value) => {
  if (label === 'Issuer Ticker') {
    return String(value || '').replace(/[^a-z0-9 ]/gi, '').slice(0, 39);
  }
  if (label === 'SEC Type') {
    return String(value || '').slice(0, 255);
  }
  if (label === 'Monthly Balance') {
    return sanitizeNumericWithScale(value, 16, 2);
  }
  if (label === 'Accrued Interest') {
    return sanitizeNumericWithScale(value, 8, 2);
  }
  if (label === 'Effective Duration') {
    return sanitizeNumericWithScale(value, 3, 4);
  }
  if (label === 'YTM at Cost' || label === 'YTM Market') {
    let val = sanitizeNumericWithScale(value, 2, 2);
    const numeric = parseFloat(val);
    if (!isNaN(numeric) && numeric > 10) {
      val = '10.00';
    }
    return val;
  }
  if (label === 'WAM') {
    return sanitizeNumericOnlyText(value, 49);
  }
  return value;
};

const ThirdPartyHoldingsDetailsModal = ({
  formValues,
  editingField,
  setEditingField,
  onFieldChange,
  ratingOptions = { sp: [], moody: [], fitch: [] },
  securityTypeOptions = [],
  fieldErrors = {},
}) => {
  const inputRef = useRef(null);
  const [draftValue, setDraftValue] = useState('');

  const handleDoubleClick = (label) => {
    setDraftValue(formValues[label] !== undefined ? String(formValues[label]) : '');
    setEditingField(label);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);
  };

  const handleBlur = (label, value) => {
    const sanitized = sanitizeByField(label, value);
    if (label === 'Monthly Balance') {
      onFieldChange(label, sanitized ? `$${formatNumericWithCommas(sanitized, 2)}` : '');
    } else if (label === 'Accrued Interest') {
      onFieldChange(label, sanitized ? `$${formatNumericWithCommas(sanitized, 2)}` : '');
    } else if (label === 'Effective Duration') {
      onFieldChange(label, formatNumericWithCommas(sanitized, 4));
    } else if (label === 'YTM at Cost' || label === 'YTM Market') {
      const numeric = parseFloat(sanitized);
      onFieldChange(label, !isNaN(numeric) ? numeric.toFixed(2) : sanitized);
    } else {
      onFieldChange(label, sanitized);
    }
    setEditingField(null);
  };

  const handleKeyDown = (e, label) => {
    if (e.key === 'Enter') {
      onFieldChange(label, sanitizeByField(label, draftValue));
      setEditingField(null);
    } else if (e.key === 'Escape') {
      setEditingField(null);
    }
  };

  return (
    <>
      <OverrideStyle />
      {DUMMY_DETAILS.map(({ label, required }) => {
        const currentValue = formValues[label] !== undefined ? formValues[label] : '';
        const isEditable = required || EXTRA_EDITABLE_FIELDS.has(label);
        const isEditing = editingField === label;
        const isRatingField = Boolean(RATING_FIELD_TO_KEY[label]);
        const isMaturityField = label === 'Maturity Date';
        const isSecTypeField = label === 'SEC Type';

        return (
          <RowWrapper key={label}>
            <Box minwidth="215px" maxwidth="215px" textalign="left" display="flex" alignitems="center">
              <Text
                label={label}
                padding="14px 0 10px 0"
                color={
                  required
                    ? `${theme.colors.clean_black}`
                    : `${theme.colors.grey_tertiary}`
                }
                fontSize={`${theme.font_size.size_xl}`}
                lineheight="24px"
              />
              {required && <RequiredAsterisk>*</RequiredAsterisk>}
            </Box>
            <EditableValue editable={isEditable}>
              {isEditable && !isEditing && !isRatingField && !isMaturityField && !isSecTypeField && (
                <EditHint>double-click to edit</EditHint>
              )}
              {isRatingField ? (
                <div style={{ padding: '8px 0 8px 0' }}>
                  <Select
                    value={currentValue || undefined}
                    onChange={(value) => onFieldChange(label, value || '')}
                    placeholder="Select"
                    style={{ width: '220px', maxWidth: '100%' }}
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: 1060 }}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {(ratingOptions[RATING_FIELD_TO_KEY[label]] || []).map((rating) => (
                      <Option key={rating} value={rating}>{rating}</Option>
                    ))}
                  </Select>
                </div>
              ) : isMaturityField ? (
                <div style={{ padding: '8px 0 8px 0' }}>
                  <DatePicker
                    value={currentValue ? moment(currentValue, 'MM/DD/YYYY', true) : null}
                    onChange={(date) => onFieldChange(label, date ? date.format('MM/DD/YYYY') : '')}
                    disabledDate={(current) => current && current < moment().startOf('day')}
                    format="MM/DD/YYYY"
                    placeholder="MM/DD/YYYY"
                    className="suffix-left"
                    dropdownClassName="single-date-picker"
                    style={{ width: '220px', maxWidth: '100%' }}
                    getPopupContainer={(trigger) => trigger.parentElement}
                  />
                </div>
              ) : isSecTypeField ? (
                <div style={{ padding: '8px 0 8px 0' }}>
                  <Select
                    value={currentValue || undefined}
                    onChange={(value) => onFieldChange(label, value || '')}
                    placeholder="Select"
                    style={{ width: '220px', maxWidth: '100%' }}
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: 1060 }}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {securityTypeOptions.map((securityType) => (
                      <Option key={securityType} value={securityType}>{securityType}</Option>
                    ))}
                  </Select>
                </div>
              ) : isEditing ? (
                <InlineInput
                  ref={inputRef}
                  value={draftValue}
                  onChange={(e) => setDraftValue(sanitizeByField(label, e.target.value))}
                  onBlur={(e) => handleBlur(label, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, label)}
                  maxLength={
                    label === 'Issuer Ticker'
                      ? 39
                      : label === 'SEC Type'
                      ? 255
                      : label === 'WAM'
                      ? 49
                      : label === 'YTM at Cost' || label === 'YTM Market'
                      ? 5
                      : undefined
                  }
                />
              ) : (
                <div
                  onDoubleClick={isEditable ? () => handleDoubleClick(label) : undefined}
                  style={{
                    padding: '14px 0 14px 0',
                    fontSize: theme.font_size.size_xl,
                    lineHeight: '20px',
                    color: theme.colors.black_secondary,
                    cursor: isEditable ? 'text' : 'default',
                    userSelect: 'none',
                    minHeight: '48px',
                  }}
                >
                  {currentValue}
                </div>
              )}
              {fieldErrors[label] && <InlineError>{fieldErrors[label]}</InlineError>}
            </EditableValue>
          </RowWrapper>
        );
      })}
    </>
  );
};

export default ThirdPartyHoldingsDetailsModal;
